import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

function GraphPage() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadExpenses() {
      const token = localStorage.getItem("token");
      const email = localStorage.getItem("email");

      if (!token || !email) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(
          buildApiUrl(`record/get?email=${encodeURIComponent(email)}`),
          {
            headers: { token },
          },
        );

        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("islogin");
          navigate("/");
          return;
        }

        const data = await res.json();
        setExpenses(Array.isArray(data?.expenses) ? data.expenses : []);
      } catch (e) {
        setError("Failed to load graph data");
      }
    }

    loadExpenses();
  }, [navigate]);

  const individualExpenses = useMemo(() => {
    return [...expenses]
      .map((item) => ({
        id: item._id || item.id || `${item.title}-${item.date}`,
        title: item.title || "Expense",
        amount: Number(item.amount ?? item.expense ?? 0),
        type: item.type || "other",
        date: item.date ? new Date(item.date).toLocaleDateString() : "",
      }))
      .filter((item) => !Number.isNaN(item.amount))
      .sort((a, b) => b.amount - a.amount);
  }, [expenses]);

  const categoryTotals = useMemo(() => {
    const map = new Map();

    expenses.forEach((item) => {
      const key = (item.type || "other").toLowerCase();
      const amount = Number(item.amount ?? item.expense ?? 0);
      map.set(key, (map.get(key) || 0) + (Number.isNaN(amount) ? 0 : amount));
    });

    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, total]) => ({ type, total }));
  }, [expenses]);

  const totalExpense = useMemo(() => {
    return expenses.reduce((sum, item) => {
      const amount = Number(item.amount ?? item.expense ?? 0);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  }, [expenses]);

  const maxExpense = Math.max(
    ...individualExpenses.map((item) => item.amount),
    1,
  );

  const pieSlices = useMemo(() => {
    const total = categoryTotals.reduce((sum, item) => sum + item.total, 0);
    let startAngle = 0;
    const colors = [
      "#2563eb",
      "#06b6d4",
      "#f97316",
      "#22c55e",
      "#ef4444",
      "#8b5cf6",
    ];

    return categoryTotals.map((item, index) => {
      const angle = total > 0 ? (item.total / total) * 360 : 0;
      const endAngle = startAngle + angle;
      const slice = {
        type: item.type,
        total: item.total,
        color: colors[index % colors.length],
        startAngle,
        endAngle,
      };
      startAngle = endAngle;
      return slice;
    });
  }, [categoryTotals]);

  const piePath = (cx, cy, radius, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M",
      cx,
      cy,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ");
  };

  function polarToCartesian(cx, cy, radius, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    };
  }

  return (
    <div className="analyticsPage">
      <div className="analyticsHeader">
        <div>
          <p className="analyticsEyebrow">Expense Analytics</p>
          <h1>Spending Graphs</h1>
          <p className="analyticsSubtext">
            Individual expense bars and category pie distribution in one view.
          </p>
        </div>
        <div className="analyticsHeaderActions">
          <button type="button" onClick={() => navigate("/dashboard")}>
            Dashboard
          </button>
          <button type="button" onClick={() => navigate("/dashboard/api")}>
            AI
          </button>
        </div>
      </div>

      {error ? <p className="analyticsError">{error}</p> : null}

      <div className="analyticsStats">
        <div className="statCard">
          <span>Total Spent</span>
          <strong>{totalExpense.toFixed(2)}</strong>
        </div>
        <div className="statCard">
          <span>Expenses</span>
          <strong>{expenses.length}</strong>
        </div>
        <div className="statCard">
          <span>Categories</span>
          <strong>{categoryTotals.length}</strong>
        </div>
      </div>

      <div className="analyticsGrid">
        <section className="panelCard panelCardWide">
          <div className="panelHeader">
            <div>
              <p className="panelLabel">Bar Chart</p>
              <h2>Individual Expense View</h2>
            </div>
            <span className="panelBadge">Sorted high to low</span>
          </div>

          <div className="barChartList">
            {individualExpenses.length === 0 ? (
              <p className="emptyState">No expense data available.</p>
            ) : (
              individualExpenses.map((item) => {
                const width = Math.max(7, (item.amount / maxExpense) * 100);
                return (
                  <div key={item.id} className="barRow">
                    <div className="barMeta">
                      <strong>{item.title}</strong>
                      <span>{item.date}</span>
                    </div>
                    <div className="barTrack">
                      <div className="barFill" style={{ width: `${width}%` }} />
                    </div>
                    <div className="barValue">{item.amount.toFixed(2)}</div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <div className="analyticsSideStack">
          <section className="panelCard">
            <div className="panelHeader">
              <div>
                <p className="panelLabel">Pie Chart</p>
                <h2>Category Split</h2>
              </div>
            </div>

            <div className="pieWrap">
              <svg
                viewBox="0 0 220 220"
                className="pieChart"
                aria-label="Expense pie chart"
              >
                <circle cx="110" cy="110" r="80" fill="#e2e8f0" />
                {pieSlices.length === 0 ? (
                  <circle cx="110" cy="110" r="80" fill="#94a3b8" />
                ) : (
                  pieSlices.map((slice) => (
                    <path
                      key={slice.type}
                      d={piePath(
                        110,
                        110,
                        80,
                        slice.startAngle,
                        slice.endAngle,
                      )}
                      fill={slice.color}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  ))
                )}
                <circle cx="110" cy="110" r="45" fill="#ffffff" />
                <text
                  x="110"
                  y="104"
                  textAnchor="middle"
                  className="pieCenterValue"
                >
                  {totalExpense.toFixed(0)}
                </text>
                <text
                  x="110"
                  y="124"
                  textAnchor="middle"
                  className="pieCenterLabel"
                >
                  Total
                </text>
              </svg>
            </div>

            <div className="legendList">
              {categoryTotals.length === 0 ? (
                <p className="emptyState">No category breakdown yet.</p>
              ) : (
                categoryTotals.map((item, index) => (
                  <div key={item.type} className="legendItem">
                    <span
                      className="legendSwatch"
                      style={{
                        backgroundColor: pieSlices[index]?.color || "#64748b",
                      }}
                    />
                    <span className="legendLabel">{item.type}</span>
                    <strong>{item.total.toFixed(2)}</strong>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="panelCard">
            <div className="panelHeader">
              <div>
                <p className="panelLabel">Quick Nav</p>
                <h2>Actions</h2>
              </div>
            </div>
            <button
              type="button"
              className="secondaryAction"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
            <button
              type="button"
              className="secondaryAction"
              onClick={() => navigate("/dashboard/api")}
            >
              Open AI
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

export default GraphPage;
